import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount } from '../../../theme/tokens';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, MailIcon, EditIcon, PrinterIcon, CheckCircleIcon, XCircleIcon, MoreHorizontalIcon, ArchiveIcon, TrashIcon, UserIcon, PhoneIcon, MapPinIcon, CreditCardIcon, TagIcon, FileTextIcon, AlertTriangleIcon, ClockIcon, PackageIcon, TruckIcon, CalendarIcon, CheckIcon } from 'lucide-react';
import { Order, OrderStatus } from './Orders';
export function OrderDetail() {
  const {
    t
  } = useTranslation('shops');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  // Mock data based on the ID or default
  const order: Order = {
    id: id || '220629-1e753',
    status: 'new',
    paymentStatus: 'paid',
    customer: {
      name: 'randy',
      email: 'randy@randy.com',
      address: 'randy',
      mobileNumber: '6596605522',
      additionalPhone: '1234',
      address2: '1234'
    },
    items: [{
      name: 'Kittens',
      quantity: 2,
      price: 111.0
    }],
    total: 222.0,
    date: '29/06/2022',
    time: '14:28',
    transaction: '62bc1b4be3c2250009f1e753',
    transactionStatus: 'confirmed',
    notes: 'Please deliver between 9 AM and 5 PM.',
    shopId: '1',
    shopName: 'Beach Shop'
  };
  const getStatusBadge = (status: OrderStatus) => {
    const styles = {
      new: 'bg-blue-50 text-blue-700 border-blue-200',
      'in-progress': 'bg-amber-50 text-amber-700 border-amber-200',
      open: 'bg-purple-50 text-purple-700 border-purple-200',
      completed: 'bg-green-50 text-green-700 border-green-200',
      cancelled: 'bg-red-50 text-red-700 border-red-200'
    };
    const labels = {
      new: 'New',
      'in-progress': 'In Progress',
      open: 'Open',
      completed: 'Completed',
      cancelled: 'Cancelled'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${styles[status]}`} data-id="element-12147" data-mp="3unbt0x1ofh7v">
        {labels[status]}
      </span>;
  };
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method';
    if (transaction.startsWith('62')) return 'Credit Card';
    return 'Bank Transfer';
  };
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing';
      case 'in-progress':
        return 'Processing';
      case 'open':
        return 'Ready for pickup';
      case 'completed':
        return 'Delivered';
      case 'cancelled':
        return 'Cancelled';
      default:
        return 'Unknown';
    }
  };
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/');
    if (parts.length !== 3) return dateString;
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const day = parseInt(parts[0]);
    const month = parseInt(parts[1]) - 1;
    const year = parseInt(parts[2]);
    return `${day} ${months[month]} ${year}`;
  };
  return <div className="max-w-[1400px] mx-auto space-y-6" data-id="element-12148" data-mp="2ar61ikvxnyzf">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-12149" data-mp="26qm5xlr0d3fe">
        <div className="flex items-center gap-4" data-id="element-12150" data-mp="26nzmoour8zhc">
          <button onClick={() => navigate('/orders')} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12151" data-mp="21q7v5vy0vfdo">
            <ArrowLeftIcon size={18} data-id="element-12152" data-mp="1rtv475tv3fmm" />
          </button>
          <div data-id="element-12153" data-mp="1rvfycuc9kwjy">
            <div className="flex items-center gap-3" data-id="element-12154" data-mp="3r67e24rg0j6i">
              <h1 className="text-xl font-semibold text-gray-900" data-id="element-12155" data-mp="3r5ntegaergos">
                {t('pages.OrderDetail.order')}
                {order.id}
              </h1>
              {getStatusBadge(order.status)}
            </div>
            <p className="text-sm text-gray-500 mt-0.5" data-id="element-12156" data-mp="3u5powatz03tm">
              {order.customer.name}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2" data-id="element-12157" data-mp="3ka4f3qintz9b">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-12158" data-mp="3k9cq4yjkizs0">
            <MailIcon size={16} data-id="element-12159" data-mp="3bg9hd3jvbb3p" />
            {t('pages.OrderDetail.send')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 text-sm font-medium transition-colors" data-id="element-12160" data-mp="3g92n9ur2ophd">
            <DownloadIcon size={16} data-id="element-12161" data-mp="36hy8ow5b76n2" />
            {t('pages.OrderDetail.download')}
          </button>
          <button className="flex items-center gap-2 px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-12162" data-mp="2siow06gbaunu">
            <CheckCircleIcon size={16} data-id="element-12163" data-mp="2wm4mel1sfys3" />
            {t('pages.OrderDetail.mark_fulfilled')}
          </button>
          <div className="relative" data-id="element-12164" data-mp="2dxkobmgzpmbm">
            <button onClick={() => setShowMoreMenu(!showMoreMenu)} aria-label="More actions" aria-expanded={showMoreMenu} className="p-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors" data-id="element-12165" data-mp="28xex27emu1u2">
              <MoreHorizontalIcon size={18} aria-hidden="true" data-id="element-12166" data-mp="2wtsnq9nbn2z0" />
            </button>

            {showMoreMenu && <>
                <div className="fixed inset-0 z-10" onClick={() => setShowMoreMenu(false)} data-id="element-12167" data-mp="2ruzif4i6ui9x" />

                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-12168" data-mp="31lkiyxhf9wvh">
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12169" data-mp="3fm50zrfijonx">
                    <PrinterIcon size={16} className="mr-3 text-gray-400" data-id="element-12170" data-mp="289vsp1ohxz9y" />
                    {t('pages.OrderDetail.print_order')}
                  </button>
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-12171" data-mp="2h9ivnrwvjd54">
                    <EditIcon size={16} className="mr-3 text-gray-400" data-id="element-12172" data-mp="2qzlqzyrgx36m" />
                    {t('pages.OrderDetail.edit_order')}
                  </button>
                  <div className="border-t border-gray-100 my-1" data-id="element-12173" data-mp="2w09nv12doe8r" />
                  <button onClick={() => setShowMoreMenu(false)} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-12174" data-mp="30120836jqz99">
                    <TrashIcon size={16} className="mr-3" data-id="element-12175" data-mp="15naggaui6z03" />
                    {t('pages.OrderDetail.cancel_order')}
                  </button>
                </div>
              </>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12176" data-mp="1334ajxmbwygw">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12177" data-mp="y8rrhoorlp5o">
          {/* Order Items */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12178" data-mp="tbrmlh3nisg5">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-12179" data-mp="oe894jes2jp9">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12180" data-mp="1lzdzxh2olbg7">
                <PackageIcon size={18} className="text-gray-500" data-id="element-12181" data-mp="1h1m080vsol1g" />
                {t('pages.OrderDetail.order_items')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12182" data-mp="180xjuzixi8le">
              <div className="space-y-4" data-id="element-12183" data-mp="17zuskqhp244y">
                {order.items.map((item, index) => <div key={index} className="flex justify-between border-b border-gray-100 pb-4 last:border-0 last:pb-0" data-id="element-12184" data-mp="nm30xoc39hw" data-mpi={index}>
                    <div data-id="element-12185" data-mp="1voxta4am29x8">
                      <div className="font-medium text-gray-900" data-id="element-12186" data-mp="1voo2iehqryqh">
                        {item.name}
                      </div>
                      <div className="text-sm text-gray-500 mt-1" data-id="element-12187" data-mp="1mparhhdyz23n">
                        {t('pages.OrderDetail.quantity')} {item.quantity} ×{' '}
                        {formatCurrencyAmount(item.price, {
                      fractionDigits: 2
                    })}
                      </div>
                    </div>
                    <div className="font-medium text-gray-900" data-id="element-12188" data-mp="2jftictnomr0s">
                      {formatCurrencyAmount(item.price * item.quantity, {
                    fractionDigits: 2
                  })}
                    </div>
                  </div>)}
              </div>

              <div className="mt-6 border-t border-gray-100 pt-4 space-y-3" data-id="element-12189" data-mp="2yckjb7d08asi">
                <div className="flex justify-between text-sm" data-id="element-12190" data-mp="333aoajhiqbmo">
                  <span className="text-gray-500" data-id="element-12191" data-mp="334vl6frb26np">
                    {t('pages.OrderDetail.subtotal')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12192" data-mp="38gr624a574me">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12193" data-mp="3h8t7o5vy6qy9">
                  <span className="text-gray-500" data-id="element-12194" data-mp="2tjicd3xd9ori">
                    {t('pages.OrderDetail.shipping')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12195" data-mp="2piopmdvnug07">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-12196" data-mp="2yhto5k7829cd">
                  <span className="text-gray-500" data-id="element-12197" data-mp="iqxmszlt2eju">
                    {t('pages.OrderDetail.tax')}
                  </span>
                  <span className="text-gray-900 font-medium" data-id="element-12198" data-mp="drb07zk37lyd">
                    {formatCurrencyAmount(0, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
                <div className="flex justify-between text-base font-semibold pt-3 border-t border-gray-100" data-id="element-12199" data-mp="3j4om3aelq0e7">
                  <span className="text-gray-900" data-id="element-12200" data-mp="3ebdev85vnhlr">
                    {t('pages.OrderDetail.total')}
                  </span>
                  <span className="text-[#9F140F]" data-id="element-12201" data-mp="35crx3f88c6m1">
                    {formatCurrencyAmount(order.total, {
                    fractionDigits: 2
                  })}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Order Timeline */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12202" data-mp="2qfr83jhfapr9">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12203" data-mp="tmz5r0i6jdmt">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12204" data-mp="tl4ukdwpmzvx">
                <ClockIcon size={18} className="text-gray-500" data-id="element-12205" data-mp="ooxkc8xa7286" />
                {t('pages.OrderDetail.order_timeline')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12206" data-mp="115ynl1pzsx42">
              <div className="space-y-6" data-id="element-12207" data-mp="1156zebyq61nt">
                <div className="flex items-start" data-id="element-12208" data-mp="15xqghiwmfexd">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12209" data-mp="15y0635dr7k2j">
                    <CheckIcon size={14} className="text-green-600" data-id="element-12210" data-mp="1aye55xxdnjof" />
                  </div>
                  <div className="ml-4" data-id="element-12211" data-mp="e8dlgdbv7tdz">
                    <p className="text-sm font-medium text-gray-900" data-id="element-12212" data-mp="i7co1ra4je29">
                      {t('pages.OrderDetail.order_placed')}
                    </p>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-12213" data-mp="n91xx3lm1lor">
                      {formatDate(order.date)} {t('pages.OrderDetail.at')}{' '}
                      {order.time}
                    </p>
                  </div>
                </div>

                {order.status === 'in-progress' || order.status === 'completed' ? <div className="flex items-start" data-id="element-12214" data-mp="1dwaadgbildfh">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-amber-100 flex items-center justify-center mt-0.5" data-id="element-12215" data-mp="1dz64etk8rhyn">
                      <CheckIcon size={14} className="text-amber-600" data-id="element-12216" data-mp="1nuzfs9m855eu" />
                    </div>
                    <div className="ml-4" data-id="element-12217" data-mp="1iwfumbfgfxnj">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12218" data-mp="1ixhdv36mjqgc">
                        {t('pages.OrderDetail.processing_started')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12219" data-mp="q80q4ity8xqg">
                        {t('pages.OrderDetail.order_is_being_processed')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12220" data-mp="1043ttt4g94fi">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12221" data-mp="cfudh6q098vr">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12222" data-mp="7fy936debffo">
                        2
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12223" data-mp="2nv2y5ngch6pl">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12224" data-mp="2nw4kio2t9fmm">
                        {t('pages.OrderDetail.processing')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12225" data-mp="2e59oirzrj1nz">
                        {t('pages.OrderDetail.waiting_to_be_processed')}
                      </p>
                    </div>
                  </div>}

                {order.status === 'completed' ? <div className="flex items-start" data-id="element-12226" data-mp="36runadikig1k">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mt-0.5" data-id="element-12227" data-mp="2st4h88drt612">
                      <CheckIcon size={14} className="text-green-600" data-id="element-12228" data-mp="2wty4czje2gsw" />
                    </div>
                    <div className="ml-4" data-id="element-12229" data-mp="1hdfw7mk2uh2w">
                      <p className="text-sm font-medium text-gray-900" data-id="element-12230" data-mp="1lgcibn8tsvbl">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-12231" data-mp="21ptslxb6i0dp">
                        {t('pages.OrderDetail.order_has_been_fulfilled')}
                      </p>
                    </div>
                  </div> : <div className="flex items-start" data-id="element-12232" data-mp="1mzcqscumt8ga">
                    <div className="flex-shrink-0 h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center mt-0.5" data-id="element-12233" data-mp="1n0ns860lu448">
                      <span className="text-xs font-medium text-gray-500" data-id="element-12234" data-mp="2fnibizloqxg4">
                        3
                      </span>
                    </div>
                    <div className="ml-4" data-id="element-12235" data-mp="2an45fn3n9xkq">
                      <p className="text-sm font-medium text-gray-500" data-id="element-12236" data-mp="26mtyo881334t">
                        {t('components.getOrderStatusBadge.completed')}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-12237" data-mp="zal6e7na9ue2">
                        {t('pages.OrderDetail.waiting_to_be_completed')}
                      </p>
                    </div>
                  </div>}
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-12238" data-mp="3au95o4f1gtyn">
          {/* Customer Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12239" data-mp="3fudoi13f60s1">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12240" data-mp="3knzu59b614ec">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12241" data-mp="3pmb0ha294wrt">
                <UserIcon size={18} className="text-gray-500" data-id="element-12242" data-mp="3ujtdbbje3w53" />
                {t('pages.OrderDetail.customer_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12243" data-mp="2ez8naq0aexq">
              <div className="flex items-center gap-3 mb-2" data-id="element-12244" data-mp="7g4zt440pu55">
                <div className="w-10 h-10 bg-[#E2231A]/10 rounded-full flex items-center justify-center text-[#9F140F] font-semibold" data-id="element-12245" data-mp="2486mefpz0ohi">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div data-id="element-12246" data-mp="3chnn4p9l6kad">
                  <div className="font-medium text-gray-900" data-id="element-12247" data-mp="3cj75qmipt7fb">
                    {order.customer.name}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12248" data-mp="37kx5hxi4vgmu">
                    {t('pages.OrderDetail.customer')}
                    {order.id.split('-')[0]}
                  </div>
                </div>
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-12249" data-mp="3vh1k7g2m4b53">
                <div className="flex items-start gap-3" data-id="element-12250" data-mp="3vf6za1rccj10">
                  <MailIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12251" data-mp="3qiqar6g8c2ne" />
                  <div data-id="element-12252" data-mp="83xrtqrq7rrs">
                    <div className="text-sm text-gray-900" data-id="element-12253" data-mp="84798pypgqlv">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12254" data-mp="42vrt7pzjbor">
                      {t('pages.OrderDetail.email')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-3" data-id="element-12255" data-mp="d43hx2icl67r">
                  <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12256" data-mp="wou3ly5bnytu" />
                  <div data-id="element-12257" data-mp="wmr66t1ub3f3">
                    <div className="text-sm text-gray-900" data-id="element-12258" data-mp="11nx3l46g3euy">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12259" data-mp="mpmksvftjbzc">
                      {t('pages.OrderDetail.mobile')}
                    </div>
                  </div>
                </div>
                {order.customer.additionalPhone && <div className="flex items-start gap-3" data-id="element-12260" data-mp="dw9jp67lp426">
                    <PhoneIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12261" data-mp="iq464helbx4y" />
                    <div data-id="element-12262" data-mp="40oivbe9q9u0">
                      <div className="text-sm text-gray-900" data-id="element-12263" data-mp="41hhtq6zjv1l">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-12264" data-mp="3r4unk11g3zv0">
                        {t('pages.OrderDetail.additional_phone')}
                      </div>
                    </div>
                  </div>}
                <div className="flex items-start gap-3" data-id="element-12265" data-mp="fh4b54l4az8b">
                  <MapPinIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12266" data-mp="fei4r89yqpro" />
                  <div data-id="element-12267" data-mp="aiszcob9ieng">
                    <div className="text-sm text-gray-900" data-id="element-12268" data-mp="afey5jec4bhl">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && <div className="text-sm text-gray-900 mt-0.5" data-id="element-12269" data-mp="j26ss69i9q0">
                        {order.customer.address2}
                      </div>}
                    <div className="text-xs text-gray-500 mt-0.5" data-id="element-12270" data-mp="3nvcyny8s8ztm">
                      {t('pages.OrderDetail.shipping_address')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Information */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12271" data-mp="38zdg1to1rha8">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12272" data-mp="34xa310lqc4mu">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12273" data-mp="34zwdb8008y8r">
                <CreditCardIcon size={18} className="text-gray-500" data-id="element-12274" data-mp="1khu4azj8zzek" />
                {t('pages.OrderDetail.payment_information')}
              </h2>
            </div>
            <div className="p-6 space-y-4" data-id="element-12275" data-mp="1tgpboey13fjg">
              <div className="flex items-start gap-3" data-id="element-12276" data-mp="1yf8wud4ssnar">
                <CreditCardIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12277" data-mp="1yequcyrjlcue" />
                <div data-id="element-12278" data-mp="10sjqjr32boj3">
                  <div className="text-sm text-gray-900" data-id="element-12279" data-mp="10t3b7fk3kr0t">
                    {getPaymentMethod(order.transaction)}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12280" data-mp="1apoftzs7g044">
                    {t('pages.OrderDetail.payment_method')}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-3" data-id="element-12281" data-mp="1fihgabgn4mae">
                <TagIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12282" data-mp="hzzetmfhw02i" />
                <div data-id="element-12283" data-mp="mzu4jmnjajlz">
                  <div className="text-sm text-gray-900 capitalize" data-id="element-12284" data-mp="mz26842jy7s0">
                    {order.paymentStatus}
                  </div>
                  <div className="text-xs text-gray-500" data-id="element-12285" data-mp="3r2sp0a2mkc16">
                    {t('pages.OrderDetail.payment_status')}
                  </div>
                </div>
              </div>
              {order.transaction && <div className="flex items-start gap-3" data-id="element-12286" data-mp="9ux8fy24zmad">
                  <FileTextIcon size={16} className="text-gray-400 mt-0.5" data-id="element-12287" data-mp="9tm2brl7y2g3" />
                  <div data-id="element-12288" data-mp="38dcaujy0ol2a">
                    <div className="text-sm text-gray-900 break-all" data-id="element-12289" data-mp="38e459rfvocqz">
                      {order.transaction}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12290" data-mp="3iaqokbsk2vqw">
                      {t('pages.OrderDetail.transaction_id')}
                    </div>
                  </div>
                </div>}
              {order.transactionStatus && <div className="flex items-start gap-3" data-id="element-12291" data-mp="2pla17sjsm51j">
                  {order.transactionStatus === 'confirmed' ? <CheckCircleIcon size={16} className="text-green-500 mt-0.5" data-id="element-12292" data-mp="2uj1r6h0vnh33" /> : order.transactionStatus === 'failed' ? <AlertTriangleIcon size={16} className="text-red-500 mt-0.5" data-id="element-12293" data-mp="17mlrn3thi2dk" /> : <ClockIcon size={16} className="text-amber-500 mt-0.5" data-id="element-12294" data-mp="xpquao8y680v" />}
                  <div data-id="element-12295" data-mp="ssqo8dc3l59r">
                    <div className="text-sm text-gray-900 capitalize" data-id="element-12296" data-mp="sta83zlb63qf">
                      {order.transactionStatus}
                    </div>
                    <div className="text-xs text-gray-500" data-id="element-12297" data-mp="jy2kirm67vn2">
                      {t('pages.OrderDetail.transaction_status')}
                    </div>
                  </div>
                </div>}
            </div>
          </div>

          {/* Shipping Status */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-12298" data-mp="51v0brariu7s">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12299" data-mp="3rjmybnok5vq7">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-12300" data-mp="3ri23ry28ucsc">
                <TruckIcon size={18} className="text-gray-500" data-id="element-12301" data-mp="edeyev2kfpk" />
                {t('pages.OrderDetail.shipping_status')}
              </h2>
            </div>
            <div className="p-6" data-id="element-12302" data-mp="3mjifvs43aqxc">
              <div className="text-sm font-medium text-gray-900 mb-4" data-id="element-12303" data-mp="3mksfilqlc763">
                {getShippingStatus(order.status)}
              </div>
              {order.notes && <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-id="element-12304" data-mp="j2id96syj8gz">
                  <div className="text-xs font-medium text-gray-700 mb-1" data-id="element-12305" data-mp="5coqqv4xd0af">
                    {t('pages.OrderDetail.customer_notes')}
                  </div>
                  <p className="text-sm text-gray-600" data-id="element-12306" data-mp="9bnptz3z62ty">{order.notes}</p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}